import 'core-js/stable'
import './index.css'

import sendMessageToSDK, { MessageName } from 'common/utils/send-message-to-sdk'

import { BillingAddressContextProvider } from 'pages/payment-bootstrap/klarna-payments/use-billing-address'
import CookieLegalNotice from 'common/components/cookie-legal-notice'
import { DesignProvider } from 'common/contexts/dark-mode'
import ErrorBoundary from 'common/components/error-boundary'
import HppContext from 'common/contexts/hpp'
import { LastInteractionContextProvider } from './pages/distribution-payment/common/use-last-interaction'
import React from 'react'
import ScreenSizeListener from 'common/components/screen-size-listener'
import ThemeLoader from 'common/components/theme-loader'
import TranslationLoader from 'common/components/translation-loader'
import captureSessionInfo from 'common/utils/capture-session-info'
import currency from 'common/utils/currency'
import { getItem } from 'common/utils/local-storage'
import { initTracker } from 'common/utils/tracker'
import { isKlapp } from 'common/utils/is-klapp'
import nativeHookApi from 'common/utils/native-hook-api'
import normalizeHPPSettings from 'common/utils/normalize-hpp-settings'
import pageNames from './pages/page-names'
import { render } from 'react-dom'
import { setGlobalBackground } from 'common/utils/global-background'
import { setDMRedsign } from 'common/utils/setDMRedesign'

const __PROD__ = process.env.NODE_ENV === 'production'
const __DEV__ = process.env.NODE_ENV === 'development'

let page

const isDarkModeExperiment =
  window.HPP_SETTINGS &&
  window.HPP_SETTINGS.experiments &&
  window.HPP_SETTINGS.experiments.some(
    experiment => experiment.feature_key === 'frontend-dark-mode-experiment'
  )

const isDemoPage = window.HPP_SETTINGS.page === pageNames.IST_DEMO_TOOL

window.isDarkMode = getItem('isDarkMode')
  ? JSON.parse(getItem('isDarkMode'))
  : isDarkModeExperiment || isKlapp() || isDemoPage
  ? window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches
  : false

setGlobalBackground(window.isDarkMode)

function handleError(error) {
  if (window.Sentry) {
    window.Sentry.captureException(error)
  }

  const { actions: { error: errorUrl = null } = {}, distribution } = window.HPP_SETTINGS

  const currentMethod = distribution?.initialState?.lastDistribution?.method

  let fallbackUrl = 'https://x.klarnacdn.net/hpp/v2/error.html'

  if (page === pageNames.DISTRIBUTION_PAYMENT_REDESIGN || page === pageNames.DISTRIBUTION_PAYMENT) {
    if (window.__remove_distribution_payment_listeners) {
      window.__remove_distribution_payment_listeners()
    }

    fallbackUrl = 'https://x.klarnacdn.net/hpp/v2/distribution-module-error.html'
  }

  if (page === pageNames.DISTRIBUTION_PAYMENT_ERROR) {
    sendMessageToSDK(MessageName.CLIENT_ERROR, {
      name: error.name,
      message: error.message,
      method: currentMethod
    })
  } else if (__PROD__) {
    // Wait for Sentry to report the error before redirecting
    setTimeout(() => {
      window.location.assign(errorUrl || fallbackUrl)
    }, 2000)
  }
}

// Needed for SDK to make modal background transparent
// eslint-disable-next-line eqeqeq
if (window.self != window.top) {
  document.body.classList.add('in-iframe')
}

try {
  const hppSettings = normalizeHPPSettings(window.HPP_SETTINGS)

  const { loadPageComponent, page: newPageToAssign } = setDMRedsign(hppSettings, page)
  page = newPageToAssign

  if (__DEV__) {
    console.log(hppSettings)
  }

  initTracker(hppSettings)

  const currentMethod = hppSettings.distribution?.initialState?.lastDistribution?.method

  if (nativeHookApi.isSupported()) {
    nativeHookApi.init()
    nativeHookApi.handshake().then(() => {
      sendMessageToSDK(MessageName.LOADED, {
        page: hppSettings.page,
        method: currentMethod,
        sessionId: hppSettings.sessionId
      })
      nativeHookApi.componentStatus({
        data: 'session',
        sessionId: hppSettings.sessionId,
        locale: hppSettings.locale,
        provider: hppSettings.provider,
        region: hppSettings.region,
        environment: hppSettings.environment
      })
    })
  }

  captureSessionInfo({
    page: hppSettings.page,
    provider: hppSettings.provider,
    issuer: hppSettings.issuer,
    correlationId: hppSettings.correlationId
  })

  if (hppSettings.order) {
    currency.configure({
      country: hppSettings.order.purchaseCountry,
      currency: hppSettings.order.purchaseCurrency,
      provider: hppSettings.provider,
      locale: hppSettings.locale
    })
  }

  loadPageComponent().then(({ default: Page }) => {
    render(
      <ErrorBoundary onError={handleError}>
        <TranslationLoader locale={hppSettings.locale}>
          <HppContext.Provider value={hppSettings}>
            <BillingAddressContextProvider>
              <LastInteractionContextProvider>
                <DesignProvider dark={window.isDarkMode}>
                  <ThemeLoader issuer={hppSettings.issuer}>
                    <ScreenSizeListener>
                      <>
                        <CookieLegalNotice />
                        <Page />
                      </>
                    </ScreenSizeListener>
                  </ThemeLoader>
                </DesignProvider>
              </LastInteractionContextProvider>
            </BillingAddressContextProvider>
          </HppContext.Provider>
        </TranslationLoader>
      </ErrorBoundary>,
      document.getElementById('root')
    )
  })
} catch (error) {
  console.error(error)
  handleError(error)
}

/*
 * This is important so we don't start the
 * visual test before the fonts have loaded
 */
// @ts-ignore
;(document.fonts ? document.fonts.ready : Promise.resolve()).then(() => {
  document.body.classList.add('fonts-loaded')
})
